import React, { useEffect, useState } from "react";
import { Transaction, Team, Player } from "../objects/types";
import { listTransactions, listTeams, listPlayers } from "../repository/api";

interface TransactionLogProps {
  year: number;
}

const TransactionLog: React.FC<TransactionLogProps> = ({ year }) => {
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [teams, setTeams] = useState<Record<string, Team>>({});
  const [players, setPlayers] = useState<Record<string, Player>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setError(null);
      try {
        const [txns, teamList, playerList] = await Promise.all([
          listTransactions(year),
          listTeams(),
          listPlayers(),
        ]);
        setTransactions(txns);
        setTeams(Object.fromEntries(teamList.map((t) => [t.uuid, t])));
        setPlayers(Object.fromEntries(playerList.map((p) => [p.uuid, p])));
      } catch (err) {
        setError(
          err instanceof Error ? err.message : "Failed to load transactions"
        );
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [year]);

  if (loading) {
    return <div className="loading">Loading transactions...</div>;
  }

  if (error) {
    return <div className="error">{error}</div>;
  }

  if (transactions.length === 0) {
    return (
      <div className="empty-state">
        No transactions recorded for {year}.
      </div>
    );
  }

  const sortedTransactions = [...transactions].sort(
    (a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
  );

  return (
    <div>
      <h2>{year} Transactions</h2>
      <div className="table-container">
        <table className="table">
          <thead>
            <tr>
              <th>Date</th>
              <th>Type</th>
              <th>Team</th>
              <th>Description</th>
              <th>KP Impact</th>
            </tr>
          </thead>
          <tbody>
            {sortedTransactions.map((txn) => (
              <tr key={txn.uuid}>
                <td>{new Date(txn.created_at).toLocaleDateString()}</td>
                <td>{formatType(txn.transaction_type)}</td>
                <td>{txn.team_uuid ? teams[txn.team_uuid]?.name || "Unknown" : "-"}</td>
                <td>{formatDescription(txn, teams, players)}</td>
                <td>{formatKpImpact(txn)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

function formatType(type: string): string {
  switch (type) {
    case "kp_grant":
      return "KP Grant";
    case "lifetime_keeper":
      return "Lifetime Keeper";
    case "one_year_keeper":
      return "One-Year Keeper";
    case "pick_trade":
      return "Pick Trade";
    default:
      return type;
  }
}

function formatDescription(
  txn: Transaction,
  teams: Record<string, Team>,
  players: Record<string, Player>
): string {
  const team = txn.team_uuid ? teams[txn.team_uuid]?.name : null;
  const player = txn.player_uuid ? players[txn.player_uuid]?.name : null;

  switch (txn.transaction_type) {
    case "kp_grant":
      return `${team || "Team"} granted ${txn.kp_amount} KP`;
    case "lifetime_keeper":
      return `${team || "Team"} keeps ${player || "player"} for life`;
    case "one_year_keeper":
      return `${team || "Team"} keeps ${player || "player"} for one year`;
    case "pick_trade":
      const details = txn.details as Record<string, string> | undefined;
      return details?.description || "Pick trade";
    default:
      return "-";
  }
}

function formatKpImpact(txn: Transaction): string {
  switch (txn.transaction_type) {
    case "kp_grant":
      return `+${txn.kp_amount || 0}`;
    case "lifetime_keeper":
      return "-3";
    case "one_year_keeper":
      return "-1";
    default:
      return "-";
  }
}

export default TransactionLog;
